import axios from 'axios';
import { getAuthToken } from '../utils/auth';
import { Question, Flow, FlowProgress, ResponsePayload, QuestionPreference, QuestionPreferenceHistory, FlowCompletionStatus, FlowRequirementsMet } from '../types/index';
import { API_BASE_URL, getApiUrl } from '../config/apiConfig';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Token ${token}`;
  }
  console.log('Request:', {
    url: config.url,
    method: config.method,
    data: config.data,
    headers: config.headers
  });
  return config;
}, (error) => {
  console.error('Request error:', error);
  return Promise.reject(error);
});

// Add response interceptor for debugging
api.interceptors.response.use(
  (response) => {
      console.log('Response:', response);
      // Check if response is JSON
      const contentType = response.headers['content-type'];
      if (contentType && !contentType.includes('application/json')) {
          console.error('Unexpected content type:', contentType);
          return Promise.reject(new Error('Unexpected response format'));
      }
      return response;
  },
  (error) => {
      console.error('Response error:', error.response || error);
      return Promise.reject(error);
  }
);

export const auth = {
  login: (usernameOrEmail: string, password: string) =>
    api.post('/api/token/', { username: usernameOrEmail, password }),
  register: (username: string, password: string, email: string, emailPreferences: boolean, organizationId?: string) =>
    api.post('/api/register/', { 
      username, 
      password, 
      email, 
      emailPreferences,
      organization_id: organizationId 
    }),
  validateOrganization: (orgId: string) =>
    api.post('/api/organizations/validate/', { org_id: orgId }),
  getOrganizations: () =>
    api.get('/api/organizations/').then(response => response.data),
};

export const questions = {
  getAll: () => api.get<Question[]>('/api/questions/'),
  getNext: () => api.get('/api/questions/next/'),
  getActive: () => api.get('/api/questions/active/'),
  create: async (data: any) => {
    if (data.use_external_url) {
      const jsonData = { ...data };
      delete jsonData.video_file;
      delete jsonData.image_file;
      delete jsonData.use_external_url;
      return api.post('/api/questions/', jsonData);
    }

    const formData = new FormData();
  
    if (data.video_file) {
      formData.append('video', data.video_file);
      delete data.video_file;
    }
    
    if (data.image_file) {
      formData.append('image', data.image_file);
      delete data.image_file;
    }
  
    delete data.use_external_url;
  
    Object.keys(data).forEach(key => {
      if (key === 'options') {
        formData.append('options', JSON.stringify(data.options));
      } else {
        formData.append(key, data[key]);
      }
    });

    return api.post('/api/questions/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  update: (id: number, data: any) => api.put(`/api/questions/${id}/`, data),
  delete: (id: number) => api.delete(`/api/questions/${id}/`),
  deactivate: (id: number) => api.post(`/api/questions/${id}/deactivate/`),
  activate: (id: number) => api.post(`/api/questions/${id}/activate/`),
  getSummary: (questionId: number) => api.get<{
    summary: string;
    summary_type: 'ai' | 'template';
    source_questions: number[];
  }>(`/api/questions/${questionId}/summary/`),
  updateSummary: (questionId: number, summary: string) => api.post<{
    summary: string;
    summary_type: 'ai' | 'template';
  }>(`/api/questions/${questionId}/summary/`, { summary }),
};

// Add this utility function
const preventDuplicateCalls = () => {
  let isSubmitting = false;
  return async (callback: () => Promise<any>) => {
    if (isSubmitting) return;
    isSubmitting = true;
    try {
      return await callback();
    } finally {
      isSubmitting = false;
    }
  };
};

export const flows = {
  getAll: () => api.get<Flow[]>('/api/flows/'),
  get: (id: number) => api.get<Flow>(`/api/flows/${id}/`),
  getFlow: (flowName: string) => api.get<Flow>(`/api/flows/${flowName}/`),
  getQuestions: (flowName: string) => api.get(`/api/flows/${flowName}/questions/`),
  getRemainingQuestionDetails: async (flowName: string) => {
    return await api.get(`/api/flows/${flowName}/remaining-question-details/`);
  },
  getProgress: async (flowName: string) => {
    return await api.get(`/api/flows/${flowName}/progress/`);
  },
  updateProgress: async (flowName: string, data: { question_id: number }) => {
    const response = await api.post(`/api/flows/${flowName}/progress/`, data);
    return response;
  },
  startFlow: async (flowName: string, data?: { question_id?: number }) => {
    const response = await api.post(`/api/flows/${flowName}/progress/`, data || {});
    return response.data;
  },
  resetFlow: async (flowName: string) => {
    return await api.delete(`/api/flows/${flowName}/progress/`);
  },
  restartFlow: (flowName: string) => {
    // First delete any incomplete progress, then start a new flow
    return api.delete(`/api/flows/${flowName}/progress/`)
      .then(() => api.post(`/api/flows/${flowName}/progress/`));
  },
  getRemainingCount: (flowName: string) =>
    api.get(`/api/flows/${flowName}/remaining-count/`),
  create: (() => {
    const prevent = preventDuplicateCalls();
    return (data: any) => prevent(() => api.post('/api/admin/flows/', data));
  })(),
  update: (id: number, data: any) => api.put(`/api/admin/flows/${id}/`, data),
  delete: (id: number) => api.delete(`/api/admin/flows/${id}/`),
  addQuestion: (flowId: number, data: { question_id: number; order: number }) =>
    api.post(`/api/admin/flows/${flowId}/add_question/`, data),
  removeQuestion: (flowId: number, questionId: number) =>
    api.delete(`/api/admin/flows/${flowId}/remove_question/`, {
      data: { question_id: questionId }
    }),
  reorderQuestions: (flowId: number, questionIds: number[]) =>
    api.post(`/api/admin/flows/${flowId}/reorder_questions/`, {
      question_ids: questionIds
    }),
  getAdminFlows: () => api.get('/api/admin/flows/'),
  getAdminFlow: (id: number) => api.get(`/api/admin/flows/${id}/`),
  deactivate: (flowId: number) => 
    api.post(`/api/admin/flows/${flowId}/deactivate/`),
  deleteFlow: (flowId: number) => 
    api.delete(`/api/admin/flows/${flowId}/delete_flow/`),
  getFlowStats: (flowName: string) => api.get(`/api/flows/${flowName}/stats/`),
  getCompletionStatus: async (flowName: string): Promise<FlowCompletionStatus> => {
    const response = await api.get<FlowCompletionStatus>(`/api/flows/${flowName}/completion-status/`);
    return response.data;
  },
  checkRequirementsMet: async (flowName: string): Promise<FlowRequirementsMet> => {
    const response = await api.get<FlowRequirementsMet>(`/api/flows/${flowName}/requirements-met/`);
    return response.data;
  },
};

export const questionPreferences = {
  getAll: (flow?: string) => {
    const params = flow ? { flow } : {};
    return api.get<QuestionPreference[]>('/api/question-preferences/', { params });
  },
  get: (id: number) => api.get<QuestionPreference>(`/api/question-preferences/${id}/`),
  create: (data: { flow: string; questions: number[]; is_active?: boolean }) =>
    api.post<QuestionPreference>('/api/question-preferences/', data),
  update: (id: number, data: { flow?: string; questions?: number[]; is_active?: boolean }) =>
    api.put<QuestionPreference>(`/api/question-preferences/${id}/`, data),
  delete: (id: number) => api.delete(`/api/question-preferences/${id}/`),
  getHistory: (id: number) => api.get<QuestionPreferenceHistory[]>(`/api/question-preferences/${id}/history/`),
};

export const videos = {
  getVideoUrl: async (videoKey: string): Promise<string> => {
    try {
      // Use the signed URL endpoint instead of the old URL endpoint
      const response = await api.get(`/api/videos/signed-url/${encodeURIComponent(videoKey)}/`, {
        headers: {
          'Accept': 'application/json',
        },
        validateStatus: (status) => {
          console.log('Response status:', status);
          return status < 500;
        }
      });
      
      // Log the complete request details
      console.log('Request details:', {
        url: response.config.url,
        baseURL: response.config.baseURL,
        headers: response.config.headers
      });
      
      // Add additional logging for debugging
      console.log('Response details:', {
        status: response.status,
        headers: response.headers,
        data: response.data,
        url: response.config.url
      });
      
      if (!response.data || !response.data.url) {
        console.error('Invalid response data:', response.data);
        throw new Error('Invalid video URL response');
      }
      
      return response.data.url;
    } catch (error) {
      console.error('Error fetching video URL:', error);
      if (axios.isAxiosError(error)) {
        const rawResponse = error.response?.data;
        console.error('Raw error response:', rawResponse);
        console.error('Status:', error.response?.status);
        console.error('Headers:', error.response?.headers);
        console.error('Request URL:', error.config?.url);
        console.error('Base URL:', error.config?.baseURL);
      }
      throw new Error(error instanceof Error ? error.message : 'Server returned invalid JSON');
    }
  },
  getSignedUrl: async (key: string) => {
    const response = await api.get(`/api/videos/signed-url/${encodeURIComponent(key)}/`);
    return response.data.url;
  }
};

export const pdfs = {
  getSignedUrl: async (key: string): Promise<string> => {
    const response = await api.get(`/api/pdfs/signed-url/${key}/`);
    return response.data.url;
  },
  upload: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('pdf', file);
    
    const response = await api.post('/api/pdfs/upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data.key;
  }
};

export const images = {
  getSignedUrl: async (key: string): Promise<string> => {
    // Clean the key by removing any path prefixes
    if (key.includes('/')) {
      // Extract just the filename from the path
      key = key.split('/').pop() || key;
    }
    
    console.log('Getting signed URL for image key:', key);
    const response = await api.get(`/api/images/signed-url/${key}/`);
    return response.data.url;
  },
  upload: async (file: File): Promise<string> => {
    console.log('Uploading image:', file.name);
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await api.post('/api/images/upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    console.log('Image upload response:', response.data);
    return response.data.key;
  }
};

export const responses = {
  create: async (data: ResponsePayload) => {
    return api.post('/api/responses/', data);
  },
  createWithAudio: async (formData: FormData) => {
    const response = await api.post('/api/responses/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  },
  getHistory: async () => {
    return api.get('/api/response-history/');
  },
  getAudioQuestions: async () => {
    return api.get('/api/audio-questions/');
  },
  submitResponseWithAudio: async (questionId: number, audioBlob: Blob, textValue?: string) => {
    const formData = new FormData();
    formData.append('audio', audioBlob);
    formData.append('data', JSON.stringify({
      question: questionId,
      text_value: textValue,
    }));

    const response = await api.post('/api/responses/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    // Ensure we have valid response data
    if (!response.data) {
      throw new Error('No response data received from server');
    }

    // If the response data is the response object itself, return it
    if (response.data.question === questionId) {
      return { data: response.data };
    }

    // Otherwise, wrap the response data
    return { 
      data: {
        question: questionId,
        text_value: textValue,
        audio_url: response.data.audio_url
      }
    };
  },
  getRemainingQuestionsCount: async () => {
    return api.get('/api/remaining-questions-count/');
  },
  getRadarChartData: async () => {
    return api.get('/api/radar-chart-data/');
  },
  transcribeAudio: async (audioBlob: Blob): Promise<{ transcription: string }> => {
    const formData = new FormData();
    formData.append('audio', audioBlob);
    const response = await api.post('/api/transcribe-audio/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    if (!response.data || typeof response.data.transcription !== 'string') {
      throw new Error('Invalid transcription response format');
    }
    
    return response.data;
  }
};

export const ai = {
  startConversation: () => 
    api.post('/api/ai/conversations/'),
  sendMessage: (conversationId: number, message: string) => 
    api.post(`/api/ai/conversations/${conversationId}/messages/`, { message }),
  getConversations: () => 
    api.get('/api/ai/conversations/'),
};

export const courses = {
  getAll: () => api.get('/api/courses/'),
  get: (id: number) => api.get(`/api/courses/${id}/`),
  getFlows: (id: number) => api.get(`/api/courses/${id}/flows/`),
  getFlowByName: (flowName: string) => api.get(`/api/courses/flow/${encodeURIComponent(flowName)}/`),
  getCourseProgress: (courseId: number) => api.get(`/api/courses/${courseId}/progress/`),
  getCourseCompletion: () => api.get(`/api/course-completion/`).then(response => {
    return response.data;
  }),
  getOrganizationCourseCompletion: (organizationId?: string, courseId?: string, page: number = 1, pageSize: number = 5) => {
    const params = new URLSearchParams();
    if (organizationId) params.append('organization_id', organizationId);
    if (courseId) params.append('course_id', courseId);
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());
    
    return api.get(`/api/admin/organization-course-completion/`, { params }).then(response => {
      return response.data;
    });
  },
  
  getLifePlanCompletionStats(organizationId?: string) {
    const params = organizationId ? { organization_id: organizationId } : {};
    return api.get('/api/admin/life-plan-completion-stats/', { params })
      .then(response => response.data)
      .catch(error => {
        console.error('Error fetching life plan completion stats:', error);
        throw error;
      });
  },
  
  getUserPaceStats(organizationId?: string) {
    const params = organizationId ? { organization_id: organizationId } : {};
    return api.get('/api/admin/user-pace-stats/', { params })
      .then(response => response.data)
      .catch(error => {
        console.error('Error fetching user pace stats:', error);
        throw error;
      });
  },
};

export const getWeeklyCheckInStatus = async (): Promise<boolean[]> => {
  try {
    const response = await api.get('/api/daily_checkin_v0/weekly_status/');
    return response.data || Array(7).fill(false);
  } catch (error) {
    console.error('Error fetching weekly check-in status:', error);
    return Array(7).fill(false);
  }
};

// Summary API functions
export const fetchQuestionSummary = async (
  questionId: number, 
  questionText: string, 
  customUserPrompt?: string, 
  customSystemPrompt?: string
): Promise<{ summary: string; summary_type: string }> => {
  // Create request body instead of query parameters
  const requestBody: Record<string, string> = {
    template: questionText
  };
  
  // Add custom prompts if provided
  if (customUserPrompt) {
    requestBody.custom_user_prompt = customUserPrompt;
  }
  
  if (customSystemPrompt) {
    requestBody.custom_system_prompt = customSystemPrompt;
  }
  
  // Use POST instead of GET to handle large prompts
  const response = await api.post(`/api/questions/${questionId}/summary/`, requestBody);
  return response.data;
};

export const updateQuestionSummary = async (questionId: number, summaryText: string): Promise<{ summary: string; message: string }> => {
  const response = await api.put(`/api/questions/${questionId}/summary/`, { summary_text: summaryText });
  return response.data;
};

// Bug Report API endpoints
export const submitBugReport = async (data: {
  question_id: number;
  submitted_response: any;
  feedback_text: string;
}) => {
  return api.post('/api/bug-reports/', data);
};

export const getUserBugReports = async () => {
  return api.get('/api/bug-reports/').then(response => response.data);
};

// Content Import API endpoints
export const importFromGoogleDrive = (data: { drive_url: string; course_name?: string; course_description?: string; skip_media_download?: boolean }) => {
  console.log('Sending import request with data:', JSON.stringify(data));
  return api.post('/api/admin/content-import/import_from_drive/', data)
    .then(response => {
      console.log('Import request successful, response:', response.status, response.statusText);
      return response.data;
    })
    .catch(error => {
      console.error('Import request failed:', error.message);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw error;
    });
};

export const getImportStatus = (jobId?: number, sinceTimestamp?: string) => {
  // Build query parameters based on what's provided
  const params: Record<string, string> = {};
  if (jobId) {
    params.job_id = jobId.toString();
  }
  if (sinceTimestamp) {
    params.since = sinceTimestamp;
  }
  
  console.log(`Checking import status${jobId ? ` for job ${jobId}` : ''}${sinceTimestamp ? ` since ${sinceTimestamp}` : ''}`);
  
  return api.get('/api/admin/content-import/import_status/', { params })
    .then(response => {
      console.log('Import status response:', response.status, response.statusText);
      console.log('Import status data:', JSON.stringify(response.data));
      return response.data;
    })
    .catch(error => {
      console.error('Import status request failed:', error.message);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw error;
    });
};

export const cancelImport = (jobId: number) => {
  console.log(`Cancelling import job ${jobId}`);
  return api.post('/api/admin/content-import/cancel_import/', { job_id: jobId })
    .then(response => {
      console.log('Cancel import response:', response.status, response.statusText);
      console.log('Cancel import data:', JSON.stringify(response.data));
      return response.data;
    })
    .catch(error => {
      console.error('Cancel import request failed:', error.message);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw error;
    });
};

// Event tracking API endpoints
export const events = {
  // Track a user event
  track: (eventType: string, eventData: any = {}) => {
    return api.post('/api/events/', {
      event_type: eventType,
      event_data: eventData
    });
  },
  
  // Get user events (or all events for staff)
  getEvents: (params?: { event_type?: string, user_id?: string, limit?: number }) => {
    return api.get('/api/events/', { params });
  }
};

export const courseService = {
  getAll: () => api.get('/api/courses/'),
  get: (id: number) => api.get(`/api/courses/${id}/`),
  getFlows: (id: number) => api.get(`/api/courses/${id}/flows/`),
  getFlowByName: (flowName: string) => api.get(`/api/courses/flow/${encodeURIComponent(flowName)}/`),
  getCourseProgress: (courseId: number) => api.get(`/api/courses/${courseId}/progress/`),
  getCourseCompletion: async () => {
    const response = await api.get('/api/course-completion/');
    return response.data;
  },
};

export default api;
